package com.bulut.mecitogullari.core.importer

import com.bulut.mecitogullari.core.util.toUtcMidnightMillis
import com.bulut.mecitogullari.data.model.CashTransaction
import com.bulut.mecitogullari.data.model.Categories
import com.bulut.mecitogullari.data.model.Project
import com.bulut.mecitogullari.data.model.TransactionType
import kotlin.math.abs
import kotlin.math.floor
import kotlinx.datetime.LocalDate

/** One raw spreadsheet cell as handed over by the platform picker. */
sealed interface ImportCell {
    data class Text(val value: String) : ImportCell
    data class Number(val value: Double) : ImportCell
}

/**
 * Imports the contractor's .xlsx ledger (first sheet, rows as raw cells).
 *
 * Expected columns (header row optional):
 *   Tarih | Tür (GELİR/GİDER/EV GİDER) | Kategori | Açıklama | Proje | Tutar
 *
 * Rules:
 *  - "Daire Satış" GELİR rows are SKIPPED entirely — apartment sales are
 *    managed inside the app, the import never touches them.
 *  - EV GİDER rows become expenses with the app's "Ev Gideri" category;
 *    the Kategori column is otherwise only used when it matches a known name.
 *  - [fixedProjectId] set (proje bazlı import): every row goes to that project.
 *    Otherwise the Proje column is matched to existing projects by name;
 *    unknown names are reported and the row is imported without a project.
 *  - Missing Tarih: a dd.MM.yyyy date inside Açıklama wins, then
 *    [defaultDateEpochMillis] (ay bazlı: the viewed month, proje bazlı: today).
 *  - Ids derive from row content, so re-importing a file never duplicates.
 */
object ExcelImport {

    fun parse(
        rows: List<List<ImportCell?>>,
        projects: List<Project>,
        fixedProjectId: String?,
        defaultDateEpochMillis: Long,
    ): ExcelImportPreview {
        val transactions = mutableListOf<CashTransaction>()
        val errors = mutableListOf<String>()
        val unknownProjects = mutableSetOf<String>()
        val projectIdByName = projects.associate { normalizeTr(it.name) to it.id }
        val occurrences = mutableMapOf<String, Int>()
        var skippedSales = 0

        rows.forEachIndexed { index, cells ->
            fun text(i: Int): String = when (val cell = cells.getOrNull(i)) {
                null -> ""
                is ImportCell.Text -> cell.value.trim()
                is ImportCell.Number -> formatCellNumber(cell.value)
            }

            val typeRaw = text(1)
            val rowType = parseRowType(typeRaw)
            if (rowType == null) {
                // Header ("Tür"), summary-only or empty rows pass silently.
                if (typeRaw.isNotBlank() && normalizeTr(typeRaw) != "TUR") {
                    errors += "Satır ${index + 1}: tür anlaşılamadı (\"$typeRaw\")"
                }
                return@forEachIndexed
            }

            val category = text(2)
            val description = text(3)
            val projectName = text(4)

            // Daire satışlarına karışmıyoruz — uygulama içinden yönetiliyorlar.
            if (rowType == RowType.INCOME && isApartmentSale(category)) {
                skippedSales++
                return@forEachIndexed
            }

            // Tarih boş bırakılabiliyor: önce açıklamadaki tarih, yoksa varsayılan.
            val date = dateValue(cells.getOrNull(0))
                ?: if (text(0).isBlank()) {
                    parseDate(description) ?: defaultDateEpochMillis
                } else {
                    errors += "Satır ${index + 1}: tarih okunamadı (\"${text(0)}\")"
                    return@forEachIndexed
                }
            val amount = amountValue(cells.getOrNull(5))
            if (amount == null || amount <= 0.0) {
                errors += "Satır ${index + 1}: tutar okunamadı (\"${text(5)}\")"
                return@forEachIndexed
            }

            val projectId = when {
                fixedProjectId != null -> fixedProjectId
                projectName.isBlank() -> null
                else -> projectIdByName[normalizeTr(projectName)]
                    .also { if (it == null) unknownProjects += projectName }
            }
            val rowKey = listOf(text(0), typeRaw, category, description, projectName, amount.toString())
                .joinToString("|")
            val occurrence = occurrences[rowKey] ?: 0
            occurrences[rowKey] = occurrence + 1

            transactions += CashTransaction(
                id = "xl-${stableHash("$rowKey#$occurrence")}",
                type = rowType.transactionType,
                amount = amount,
                description = description.ifBlank { category.ifBlank { "İçe aktarılan işlem" } },
                category = resolveCategory(rowType, category),
                projectId = projectId,
                dateEpochMillis = date,
            )
        }

        return ExcelImportPreview(
            transactions = transactions,
            skippedSaleCount = skippedSales,
            incomeTotal = transactions
                .filter { it.type == TransactionType.INCOME }
                .sumOf { it.amount },
            expenseTotal = transactions
                .filter { it.type == TransactionType.EXPENSE }
                .sumOf { it.amount },
            houseExpenseCount = transactions.count { it.category == HOUSE_EXPENSE },
            houseExpenseTotal = transactions
                .filter { it.category == HOUSE_EXPENSE }
                .sumOf { it.amount },
            unknownProjects = unknownProjects.toList(),
            errors = errors,
        )
    }

    private const val HOUSE_EXPENSE = "Ev Gideri"

    private enum class RowType(val transactionType: TransactionType) {
        INCOME(TransactionType.INCOME),
        EXPENSE(TransactionType.EXPENSE),
        HOUSE(TransactionType.EXPENSE),
    }

    private fun parseRowType(raw: String): RowType? {
        val normalized = normalizeTr(raw)
        return when {
            normalized.startsWith("GELIR") -> RowType.INCOME
            normalized.startsWith("EV") && normalized.contains("GIDER") -> RowType.HOUSE
            normalized.startsWith("GIDER") -> RowType.EXPENSE
            else -> null
        }
    }

    private fun isApartmentSale(category: String): Boolean {
        val normalized = normalizeTr(category)
        return normalized.contains("DAIRE") && normalized.contains("SATI")
    }

    /** EV GİDER → "Ev Gideri"; otherwise the Kategori column only counts when
     *  it matches a known app category. */
    private fun resolveCategory(rowType: RowType, raw: String): String {
        if (rowType == RowType.HOUSE) return HOUSE_EXPENSE
        val known = if (rowType == RowType.INCOME) Categories.income else Categories.expense
        return known.firstOrNull { normalizeTr(it) == normalizeTr(raw) }
            ?: if (rowType == RowType.INCOME) "Diğer Gelir" else "Diğer Gider"
    }

    /** 5000000.0 → "5000000", 2.5 → "2.5" — for numeric cells shown as text. */
    private fun formatCellNumber(value: Double): String =
        if (value == floor(value) && abs(value) < 1e15) value.toLong().toString() else value.toString()

    private fun dateValue(cell: ImportCell?): Long? = when (cell) {
        null -> null
        is ImportCell.Number -> serialToEpochMillis(cell.value)
        is ImportCell.Text -> parseDate(cell.value)
    }

    /** Excel date serial (days since 1899-12-30) → UTC-midnight epoch millis. */
    private fun serialToEpochMillis(serial: Double): Long? {
        // Sanity window ≈ 1954..2119 so a stray amount is not read as a date.
        if (serial < 20_000 || serial > 80_000) return null
        return (floor(serial).toLong() - 25_569L) * 86_400_000L
    }

    private fun amountValue(cell: ImportCell?): Double? = when (cell) {
        null -> null
        is ImportCell.Number -> cell.value
        is ImportCell.Text -> parseAmount(cell.value)
    }

    private val DAY_FIRST_DATE = Regex("""(\d{1,2})[./-](\d{1,2})[./-](\d{4})""")
    private val ISO_DATE = Regex("""(\d{4})-(\d{1,2})-(\d{1,2})""")

    /** "13.12.2025", "1/7/2026" or "2026-07-01" → UTC-midnight epoch millis. */
    private fun parseDate(raw: String): Long? {
        val text = raw.trim()
        ISO_DATE.matchEntire(text)?.let { match ->
            val (year, month, day) = match.destructured
            return localDateOrNull(year.toInt(), month.toInt(), day.toInt())
        }
        DAY_FIRST_DATE.find(text)?.let { match ->
            val (day, month, year) = match.destructured
            return localDateOrNull(year.toInt(), month.toInt(), day.toInt())
        }
        return null
    }

    private fun localDateOrNull(year: Int, month: Int, day: Int): Long? =
        runCatching { LocalDate(year, month, day).toUtcMidnightMillis() }.getOrNull()

    private val GROUPED_NUMBER = Regex("""-?\d{1,3}(\.\d{3})+""")
    private val US_GROUPED_NUMBER = Regex("""-?\d{1,3}(,\d{3})+(\.\d+)?""")

    /** "5.000.000₺", "163.755", "600 TL", "2.250,50", "5,000,000" → Double. */
    private fun parseAmount(raw: String): Double? {
        val cleaned = raw
            .replace("₺", "")
            .replace("TL", "", ignoreCase = true)
            .replace(" ", "")
            .replace(" ", "")
            .trim()
        if (cleaned.isEmpty()) return null
        return when {
            US_GROUPED_NUMBER.matchEntire(cleaned) != null ->
                cleaned.replace(",", "").toDoubleOrNull()
            cleaned.contains(',') -> cleaned.replace(".", "").replace(',', '.').toDoubleOrNull()
            GROUPED_NUMBER.matchEntire(cleaned) != null -> cleaned.replace(".", "").toDoubleOrNull()
            else -> cleaned.toDoubleOrNull()
        }
    }

    /** Case- and diacritic-insensitive Turkish comparison key. */
    private fun normalizeTr(text: String): String = text.trim()
        .map { c ->
            when (c) {
                'i', 'İ', 'ı', 'I' -> 'I'
                'ş', 'Ş' -> 'S'
                'ğ', 'Ğ' -> 'G'
                'ü', 'Ü' -> 'U'
                'ö', 'Ö' -> 'O'
                'ç', 'Ç' -> 'C'
                else -> c.uppercaseChar()
            }
        }
        .joinToString("")
        .replace(Regex("""\s+"""), " ")

    /** FNV-1a — stable across platforms, keeps re-imports idempotent. */
    private fun stableHash(input: String): String {
        var hash = -3750763034362895579L
        for (ch in input) {
            hash = hash xor ch.code.toLong()
            hash *= 1099511628211L
        }
        return hash.toULong().toString(16)
    }
}

data class ExcelImportPreview(
    val transactions: List<CashTransaction>,
    /** "Daire Satış" rows in the file — deliberately not imported. */
    val skippedSaleCount: Int,
    val incomeTotal: Double,
    val expenseTotal: Double,
    val houseExpenseCount: Int,
    val houseExpenseTotal: Double,
    val unknownProjects: List<String>,
    val errors: List<String>,
) {
    val isEmpty: Boolean get() = transactions.isEmpty()
}
